/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, Play, ShieldCheck, VideoOff } from 'lucide-react';
import { initials } from '@/lib/utils-cat';

/**
 * Placeholder centrale mostrato finché il video remoto non è connesso:
 * avatar del partecipante, stato sala, CTA "Avvia visita" (medico) e notice di segnalazione.
 */
export const RemotePartyPlaceholder = ({ otherParty, otherName, roomActive, isDoctor, isPatient, onBeginVisit, startLoading, rtc, signalingLabel }) => {
  const { t } = useTranslation();
  return (
    <div className="relative flex flex-col items-center gap-3" data-testid="remote-party-placeholder">
      <Avatar className="h-40 w-40 md:h-48 md:w-48 ring-4 ring-white/10 shadow-2xl">
        {otherParty?.avatar_base64 && <AvatarImage src={otherParty.avatar_base64} />}
        <AvatarFallback className="bg-primary-gradient text-primary-foreground font-bold text-5xl">{initials(otherName)}</AvatarFallback>
      </Avatar>
      <div className="text-center">
        <div className="font-display font-bold text-2xl">{otherName}</div>
        <div className="text-sm text-white/60 mt-1">
          {roomActive ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
              {t('videoConnected')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-warning">
              <Clock className="h-3 w-3" />
              {t('videoWaitingDoctor')}
            </span>
          )}
        </div>
      </div>
      {!roomActive && isDoctor && (
        <Button size="lg" variant="accent" onClick={onBeginVisit} disabled={startLoading} className="mt-4 pulse-glow" data-testid="start-visit-btn">
          <Play className="h-4 w-4" /> {startLoading ? t('processing') : t('visitStart')}
        </Button>
      )}
      {!roomActive && isPatient && (
        <div className="mt-4 px-4 py-2 rounded-full glass-strong text-sm text-white/80">
          {t('videoWaitingDoctorLong')}
        </div>
      )}
      {rtc.error && (
        <div className="absolute -bottom-20 left-0 right-0 text-center text-[10px] uppercase tracking-widest text-red-400" data-testid="rtc-error">
          Errore: {rtc.error}
        </div>
      )}
      {!rtc.error && !rtc.connected && (
        <div className="absolute -bottom-20 left-0 right-0 text-center text-[10px] uppercase tracking-widest text-white/40">
          {signalingLabel}
        </div>
      )}
    </div>
  );
};

/** Barra superiore: badge LIVE + notice E2E. */
export const RoomTopBar = () => {
  const { t } = useTranslation();
  return (
    <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-start gap-2 z-10" data-testid="room-top-bar">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full glass-strong">
        <span className="h-2 w-2 rounded-full bg-emergency animate-pulse" />
        <span className="text-xs font-semibold text-white">{t('videoLive')}</span>
      </div>
      <Badge className="glass-strong text-white border-0 hidden md:inline-flex" variant="outline">
        <ShieldCheck className="h-3 w-3 mr-1 text-accent" /> {t('videoE2ENotice')}
      </Badge>
    </div>
  );
};

/** Anteprima locale (picture-in-picture). */
export const SelfViewPip = ({ rtc, camOn }) => {
  const { t } = useTranslation();
  return (
    <div className="absolute top-16 right-4 md:top-6 md:right-6 w-32 h-44 md:w-40 md:h-56 rounded-2xl overflow-hidden bg-slate-800 shadow-2xl border border-white/20 z-20" data-testid="self-view-pip">
      {rtc.connected && camOn ? (
        <video ref={rtc.localVideoRef} autoPlay muted playsInline className="w-full h-full object-cover [transform:scaleX(-1)]" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-slate-800">
          <VideoOff className="h-8 w-8 text-white/50" />
        </div>
      )}
      <div className="absolute bottom-1 left-1 right-1 text-[9px] uppercase font-bold text-white/80 bg-black/40 rounded-md text-center py-0.5">
        {t('videoYou')}
      </div>
    </div>
  );
};
